import React from 'react';
import { motion } from 'framer-motion';
import ProjectStatsBadge from '../../../components/ProjectStatsBadge';
import ShareProjectButton from '../../../components/ShareProjectButton/ShareProjectButton';
import './rfid-based-bus-number-announce-system-for-blind.css';
import rfidBlindImg from '../../../assets/images/RFID Image.png';

const RFIDBasedBusNumberAnnounceSystemForBlind = () => {
  return (
    <motion.div 
      className="project-detail-container"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
    >
      <motion.h1 
        className="project-heading"
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.2 }}
      >
        RFID Based <span>Bus Number Announce</span> System for Blind
      </motion.h1>

      <motion.p 
        className="last-updated"
        initial={{ x: 50, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.4 }}
      ><br />
        Last Updated On: 10th December 2024
      </motion.p>

      <div className="project-content">
        <motion.div 
          className="project-left"
          initial={{ x: -100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <img src={rfidBlindImg} alt="RFID Based System for Blind" className="project-image" />
          <h2 className="project-subtitle">RFID Based Bus Number Announce System for Blind</h2>
          <span className="project-category">Embedded Systems</span>
          <p className="project-author">Author: Praveen Kumar B</p>

          <ProjectStatsBadge docId="rfidBusSystem" trackView={true} />

          {/* Buttons */}
          <div className="project-buttons">
            <a href="/under-construction" className="visit-project-button">
              → Visit the Project
            </a>
            <a href="https://github.com/Praveenkumar-king/RFID-Based-Bus-Number-Announce-System-For-Blind" className="visit-project-button">
              → View on GitHub
            </a>
            <ShareProjectButton
              project={{
                id: "rfidBusSystem",
                title: "RFID Based Bus Number Announce System For Blind",
                path: "/projects/projectdetail/rfid-based-bus-number-announce-system-for-blind"
              }}
            />
          </div>
        </motion.div>

        <motion.div 
          className="project-right"
          initial={{ x: 100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          <h3 className="section-title">Project Details</h3>
          <p className="overview-text">
            Hi Friends & Family 🎉🎉, Here's The Detailed Explanation Of The Project.
          </p>

          <h3 className="section-title">Overview 🌟</h3>
          <p className="overview-description">
            The <strong>RFID-Based Bus Number Announcement System For Blind</strong> is a revolutionary project designed to assist visually impaired individuals in identifying buses with ease and accuracy. This system uses RFID technology to announce the bus number when a bus approaches a designated bus stop, ensuring convenience and safety for blind passengers.
          </p>

          <h3 className="section-title">🌟 Key Highlights of the Project</h3>
          <ul className="feature-list">
            <li><strong>📖 Introduction</strong></li>
            <ul>
              <li><strong>Problem Addressed:</strong> Visually impaired individuals face challenges in identifying buses at bus stops, leading to inconvenience and safety risks.</li>
              <li><strong>Proposed Solution:</strong> The RFID system provides an audible announcement of the bus number to inform and assist blind passengers effectively.</li>
            </ul>

            <li><strong>🛠️ How the System Works</strong></li>
            <ul>
              <li><strong>RFID Technology:</strong> Each bus is equipped with an RFID tag that contains its unique bus number.</li>
              <li><strong>RFID Reader:</strong> Installed at bus stops, the reader detects approaching buses.</li>
              <li><strong>Announcement System:</strong> A speaker or audio device announces the bus number as detected by the RFID reader.</li>
              <li><strong>Power Supply:</strong> Operates on a reliable and efficient power source, ensuring uninterrupted functionality.</li>
            </ul>
          </ul>

          <h3 className="section-title">🚀 Features and Benefits</h3>
          <ul className="benefits-list">
            <li><strong>Accessibility Enhancements:</strong> Designed specifically for visually impaired users to make public transport more inclusive.</li>
            <li><strong>Real-Time Announcements:</strong> Provides instant and accurate bus number announcements upon bus arrival.</li>
            <li><strong>Low Maintenance:</strong> Easy to set up and maintain, making it practical for urban and rural areas.</li>
            <li><strong>Cost-Effective:</strong> Utilizes affordable RFID technology to deliver high-impact results.</li>
            <li><strong>User-Friendly:</strong> Requires minimal interaction, ensuring ease of use for all passengers.</li>
          </ul>

          <h3 className="section-title">⚙️ Technical Details</h3>
          <ul className="how-it-works-list">
            <li><strong>Hardware Components:</strong></li>
            <ul>
              <li>RFID tags and readers</li>
              <li>Microcontroller for processing data</li>
              <li>Audio output device for announcements</li>
            </ul>
            <li><strong>Software Components:</strong></li>
            <ul>
              <li>Code for tag detection and audio conversion</li>
              <li>Firmware to ensure smooth integration between hardware and software</li>
            </ul>
            <li><strong>Power Supply:</strong> Uses a battery backup for reliability during power outages.</li>
          </ul>

          <h3 className="section-title">🌈 Advantages</h3>
          <ul className="benefits-list">
            <li><strong>Improved Mobility:</strong> Helps visually impaired individuals navigate public transport with confidence.</li>
            <li><strong>Enhanced Safety:</strong> Reduces the risk of boarding the wrong bus or missing the correct one.</li>
            <li><strong>Scalability:</strong> Can be implemented at multiple bus stops with ease.</li>
            <li><strong>Integration:</strong> Can work alongside existing public transport systems.</li>
          </ul>

          <h3 className="section-title">🌍 Real-Life Applications</h3>
          <ul className="how-it-works-list">
            <li>Urban Transport: Ideal for busy metropolitan bus stops.</li>
            <li>Rural Areas: Assists visually impaired passengers in less connected regions.</li>
            <li>School and Corporate Transport: Ensures accessibility for students and employees.</li>
          </ul>

          <h3 className="section-title">🔨 Implementation Steps</h3>
          <ul className="how-it-works-list">
            <li>Install RFID readers at key bus stops.</li>
            <li>Attach RFID tags to all buses with unique identification numbers.</li>
            <li>Connect the RFID reader to a microcontroller and audio system.</li>
            <li>Program the microcontroller to recognize RFID data and trigger announcements.</li>
            <li>Test the system in real-world scenarios to ensure reliability.</li>
          </ul>

          <h3 className="section-title">🔭 Future Scope</h3>
          <ul className="how-it-works-list">
            <li>Integration with Mobile Apps: Provide additional notifications through mobile devices for enhanced user experience.</li>
            <li>Multiple Language Support: Announcements in regional languages to cater to diverse populations.</li>
            <li>GPS Integration: Use GPS to provide advanced notifications about bus arrival times.</li>
          </ul>

          <h3 className="section-title">Conclusion 🎯</h3>
          <p>
            The <strong>RFID Based Bus Number Announce System for Blind</strong> offers a smart and practical solution to assist the visually impaired in their daily commute. With simplicity, effectiveness, and affordability at its core, it's a step toward accessible public transportation for all. 🌍
          </p>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default RFIDBasedBusNumberAnnounceSystemForBlind;
